import { PabsMessageText } from './PabsResponse';
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  HStack,
  Button,
  Spinner,
  VStack,
  Divider,
  Select,
  Switch,
  Collapse,
  useToast,
} from "@chakra-ui/react";
import ReactMarkdown from "react-markdown";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { getConnectorLogo } from "../utils/connectorLogos";
import { governedTaskStatus } from "../services/JobLifecycleProjection";
import FloatingCardActions from "./FloatingCardActions";
import { ACCENT_CARD_BACKGROUND, ACCENT_CARD_CONTENT_PROPS, AccentCardLabel, COMPACT_CONTENT_MAX_WIDTH, isNarrowPanel } from "./PageShell";

const DAYS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const HOURS = Array.from({ length: 24 }, (_, i) => {
  const ampm = i < 12 ? "AM" : "PM";
  const h = i % 12 === 0 ? 12 : i % 12;
  return { value: i, label: `${h}:00 ${ampm}` };
});

const FREQUENCIES = [
  { value: "daily",    label: "Every day" },
  { value: "weekly",   label: "Once a week" },
  { value: "biweekly", label: "Every two weeks" },
  { value: "monthly",  label: "Once a month" },
];

const DAYS_OF_MONTH = Array.from({ length: 28 }, (_, i) => ({
  value: i + 1,
  label: `${i + 1}${[,"st","nd","rd"][((i+1)%100>>3^1)&&(i+1)%10]||"th"}`,
}));

// Module-level lock — survives StrictMode's unmount/remount cycle
const fetchingPlans = new Set();

const ConnectedIntegrationIcons = ({ integrations = [] }) => (
  <Flex align="center" ml={0.5}>
    {integrations.slice(0, 3).map((integration, index) => {
      const logo = getConnectorLogo(integration.id);
      return (
        <Box
          key={integration.id}
          w="16px"
          h="16px"
          ml={index === 0 ? 0 : "-5px"}
          borderRadius="full"
          bg={logo?.bg || integration.iconBg || "gray.500"}
          border="1.5px solid"
          borderColor="gray.50"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          zIndex={3 - index}
          title={integration.name}
        >
          {logo?.Logo
            ? <logo.Logo size={9} />
            : <Text fontSize="7px" color="white" fontWeight="bold">{integration.name?.[0]}</Text>}
        </Box>
      );
    })}
  </Flex>
);

const LOADING_MESSAGES = [
  "Reading your strategy...",
  "Checking what's moved since last time...",
  "Looking at your assumptions and learnings...",
  "Forming a verdict...",
];

// ---------------------------------------------------------------------------
// Markdown renderer — minimal, prose-only
// ---------------------------------------------------------------------------
const PulseMarkdown = ({ content, dark = false }) => (
  <ReactMarkdown
    components={{
      p: ({ children }) => (
        <Text
          as="div"
          fontSize={{ base: "sm", md: "md" }}
          lineHeight="1.9"
          color={dark ? "whiteAlpha.800" : "gray.700"}
          mb={3}
        >
          {children}
        </Text>
      ),
      a: ({ href, children }) => (
        <Text
          as="span"
          color={dark ? "white" : "brand.500"}
          cursor="pointer"
          textDecoration="underline"
          textDecorationStyle="dotted"
          _hover={{ color: dark ? "whiteAlpha.700" : "brand.700" }}
          onClick={() => {
            if (href && !href.startsWith("http")) {
              window.__pabsNavigate?.(href);
            } else if (href) {
              window.open(href, "_blank");
            }
          }}
        >
          {children}
        </Text>
      ),
      hr: () => (
        <Divider borderColor={dark ? "whiteAlpha.100" : "gray.100"} my={6} />
      ),
      strong: ({ children }) => (
        <Text as="strong" fontWeight="semibold" color={dark ? "white" : "gray.800"}>
          {children}
        </Text>
      ),
    }}
  >
    {content}
  </ReactMarkdown>
);

const TONES = [
  { value: "direct",     label: "Direct — short, blunt, no softening" },
  { value: "analytical", label: "Analytical — clear finding, real implication" },
  { value: "coaching",   label: "Coaching — honest, specific, one thing at a time" },
];

const STRATEGY_STATUS_LABELS = {
  exploring: "Still exploring",
  alive: "Still active",
  parked: "Parked for now",
  got_what_i_needed: "Got enough clarity",
  not_pursuing: "Not pursuing",
};

const buildStrategyStatusChatPrompt = (status) => {
  const label = STRATEGY_STATUS_LABELS[status];
  if (!label) return "";
  return `I marked this strategy as "${label}" from Pulse. Please register this as the current outcome/status for the strategy. Do not create a Pulse, agent task, reminder, or follow-up unless I explicitly ask for one.`;
};

// ---------------------------------------------------------------------------
// Shared settings form — used for first-time setup and gear panel
// ---------------------------------------------------------------------------
const PulseSettingsForm = ({ planId, initial = {}, intro, onSaved, onClose }) => {
  const [frequency, setFrequency] = useState(initial.frequency || "weekly");
  const [day, setDay]             = useState(initial.day ?? 1);
  const [dayOfMonth, setDayOfMonth] = useState(initial.dayOfMonth ?? 1);
  const [hour, setHour]           = useState(initial.hour ?? 9);
  const [tone, setTone]           = useState(initial.tone || "direct");
  const [emailOn, setEmailOn]     = useState(initial.email ?? false);
  const [saving, setSaving]       = useState(false);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetchWithAuth(`/api/pulse/${planId}/schedule`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          frequency,
          day_of_week: frequency === "monthly" ? dayOfMonth : day,
          hour,
          timezone,
          email: emailOn,
          tone,
        }),
      });
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error("Failed to save pulse settings:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <VStack align="start" spacing={4} maxW="400px">
      {intro && (
        <Text fontSize="md" color="brand.800" fontStyle="normal">{intro}</Text>
      )}

      {/* Tone */}
      <Box w="full">
        <Text fontSize="xs" fontStyle="bold" color="brand.600" mb={1} textTransform="uppercase" letterSpacing="wider">Tone</Text>
        <Select
          size="sm"
          value={tone}
          onChange={e => setTone(e.target.value)}
          borderColor="gray.200"
          color="gray.700"
          fontSize="sm"
        >
          {TONES.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </Select>
      </Box>

      {/* Frequency */}
      <Box w="full">
        <Text fontSize="xs" fontStyle="bold" color="brand.600" mb={1} textTransform="uppercase" letterSpacing="wider">Check-in frequency</Text>
        <Select
          size="sm"
          value={frequency}
          onChange={e => setFrequency(e.target.value)}
          borderColor="gray.200"
          color="gray.700"
          fontSize="sm"
        >
          {FREQUENCIES.map(f => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </Select>
      </Box>

      {/* Day + hour — weekly/biweekly */}
      {(frequency === "weekly" || frequency === "biweekly") && (
        <HStack spacing={3} w="full">
          <Select size="sm" value={day} onChange={e => setDay(Number(e.target.value))}
            borderColor="gray.200" color="gray.700" fontSize="sm">
            {DAYS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </Select>
          <Select size="sm" value={hour} onChange={e => setHour(Number(e.target.value))}
            borderColor="gray.200" color="gray.700" fontSize="sm">
            {HOURS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
          </Select>
        </HStack>
      )}

      {/* Hour only — daily */}
      {frequency === "daily" && (
        <Select size="sm" value={hour} onChange={e => setHour(Number(e.target.value))}
          borderColor="gray.200" color="gray.700" fontSize="sm" maxW="160px">
          {HOURS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
        </Select>
      )}

      {/* Day of month + hour — monthly */}
      {frequency === "monthly" && (
        <HStack spacing={3} w="full">
          <Select size="sm" value={dayOfMonth} onChange={e => setDayOfMonth(Number(e.target.value))}
            borderColor="gray.200" color="gray.700" fontSize="sm">
            {DAYS_OF_MONTH.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </Select>
          <Select size="sm" value={hour} onChange={e => setHour(Number(e.target.value))}
            borderColor="gray.200" color="gray.700" fontSize="sm">
            {HOURS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
          </Select>
        </HStack>
      )}

      <Text fontSize="xs" color="gray.400">{timezone}</Text>

      <Box w="full">
        <Text fontSize="xs" fontStyle="bold" color="brand.600" mb={1} textTransform="uppercase" letterSpacing="wider">Notifications</Text>
        <HStack border="1px solid" borderColor="gray.200" borderRadius="lg" px={3} py={3} justify="space-between" w="full">
          <HStack spacing={2.5}>
            <span className="material-icons-outlined" style={{ fontSize: '18px', color: '#4A5568' }}>mail</span>
            <Box>
              <Text fontSize="sm" fontWeight="500" color="gray.700">Email run summaries</Text>
              <Text fontSize="xs" color="gray.500">Push notifications remain available in Pabs.</Text>
            </Box>
          </HStack>
          <Switch size="sm" isChecked={emailOn} onChange={e => setEmailOn(e.target.checked)} colorScheme="blue" />
        </HStack>
      </Box>

      <HStack spacing={3}>
        <Button size="sm" variant="solid" colorScheme="gray" isLoading={saving} onClick={handleSave}>
          Save Schedule
        </Button>
        {onClose && (
          <Button size="sm" variant="ghost" colorScheme="gray" onClick={onClose}>
            Cancel
          </Button>
        )}
      </HStack>
    </VStack>
  );
};

// ---------------------------------------------------------------------------
// Previous pulses archive
// ---------------------------------------------------------------------------
const PulseArchive = ({ planId }) => {
  const [open, setOpen]       = useState(false);
  const [pulses, setPulses]   = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (pulses.length > 0) return;
    setLoading(true);
    try {
      const res = await fetchWithAuth(`/api/pulse/${planId}/history`);
      const data = await res.json();
      setPulses(data.pulses || []);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (!open) load();
    setOpen(p => !p);
  };

  if (pulses.length === 0 && !open) return null;

  return (
    <Box mt={12}>
      <Text
        fontSize="xs"
        color="gray.400"
        cursor="pointer"
        _hover={{ color: "gray.600" }}
        onClick={handleToggle}
        userSelect="none"
      >
        {open ? "Hide" : "Previous pulses"}
      </Text>

      <Collapse in={open}>
        {loading ? (
          <Spinner size="sm" color="gray.300" mt={4} />
        ) : (
          <VStack align="stretch" spacing={8} mt={6}>
            {pulses.map(p => (
              <Box key={p.id} opacity={0.6}>
                <Text fontSize="xs" color="gray.400" mb={3}>
                  {new Date(p.created_at).toLocaleDateString("en-NZ", {
                    day: "numeric", month: "long", year: "numeric"
                  })}{" "}
                  {new Date(p.created_at).toLocaleTimeString("en-NZ", {
                    hour: "numeric", minute: "2-digit", hour12: true
                  })}
                </Text>
                <Box fontSize="sm" color="gray.500">
                  <PulseMarkdown content={p.content} />
                </Box>
              </Box>
            ))}
          </VStack>
        )}
      </Collapse>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main PulseView
// ---------------------------------------------------------------------------
const PulseView = ({
  planId,
  contentPanelWidth = 1000,
  onSendChatPrompt,
  onNavigateToView,
  onOpenJobsWorkflows,
  strategyLifecycleStatus = "exploring",
  onStrategyLifecycleStatusChange,
}) => {
  // Sized against the content area, not the window: the narrative panel can make
  // this view far narrower than the viewport suggests.
  const isNarrow = isNarrowPanel(contentPanelWidth);
  const [pulse, setPulse]             = useState(null);
  const [schedule, setSchedule]       = useState(null);
  const [loading, setLoading]         = useState(true);
  const [generating, setGenerating]   = useState(false);
  const [loadingMsg, setLoadingMsg]   = useState(LOADING_MESSAGES[0]);
  const [error, setError]                   = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [sourcesExpanded, setSourcesExpanded] = useState(false);
  const [floatingActionsDismissed, setFloatingActionsDismissed] = useState(false);
  const [remindedAt, setRemindedAt] = useState(null);
  const [remindedTaskId, setRemindedTaskId] = useState(null);
  const [navigationSummary, setNavigationSummary] = useState({
    blockers: 0,
    next_steps: 0,
    learnings: 0,
  });
  const [jobsCount, setJobsCount] = useState(0);
  // Bumped when a Pulse arrives, so counts created alongside it show at once.
  const [overviewRefreshKey, setOverviewRefreshKey] = useState(0);
  const [connectedIntegrations, setConnectedIntegrations] = useState([]);
  const [designAuthorityState, setDesignAuthorityState] = useState({ enabled: false, activeJobCount: 0, needsAttention: false });
  const toast = useToast();

  // Tracks the planId that was last committed by the planId effect.
  // Used to distinguish a genuine plan switch from StrictMode's artificial
  // re-run of the same planId, which would wipe the lock fetchPulse just set.
  const committedPlanIdRef = useRef(undefined);

  // Always reflects the LATEST planId — updated inline on every render so
  // stale async closures (fetchPulse / generatePulse from old plans) can check
  // whether they are still the active plan before touching state.
  const currentPlanIdRef = useRef(planId);
  currentPlanIdRef.current = planId;

  // Expose navigation to markdown link handler
  useEffect(() => {
    window.__pabsNavigate = (dest) => onNavigateToView?.(dest);
    return () => { delete window.__pabsNavigate; };
  }, [onNavigateToView]);

  // Reset state on plan switch and clear any stale client-side lock.
  //
  // Lock-clearing rules:
  //   • committedPlanIdRef starts as `undefined` on every fresh mount/remount.
  //     undefined !== planId is always true, so the lock is ALWAYS cleared on
  //     initial mount — important when the component re-instantiates and the
  //     module-level Set still holds a lock from the previous instance.
  //   • On StrictMode's artificial second run, committedPlanIdRef.current has
  //     already been set to planId in the first run, so planId !== planId is
  //     false → we skip the delete, keeping the lock fetchPulse just set.
  //   • On a genuine plan switch the old planId !== new planId → we delete the
  //     new plan's lock so fetchPulse can run fresh.
  useEffect(() => {
    const planActuallyChanged = committedPlanIdRef.current !== planId;

    if (planActuallyChanged && planId) fetchingPlans.delete(planId);
    committedPlanIdRef.current = planId;

    setGenerating(false);
    setLoading(true);
    setPulse(null);
    setError(null);
    setRemindedAt(null);
    setRemindedTaskId(null);
    setNavigationSummary({ blockers: 0, next_steps: 0, learnings: 0 });
    setDesignAuthorityState({ enabled: false, activeJobCount: 0, needsAttention: false });
  }, [planId]);

  useEffect(() => {
    if (!planId) return undefined;

    const refreshOverviewMetadata = async () => {
      const [jobsResponse, connectorsResponse, designAuthorityResponse] = await Promise.all([
        fetchWithAuth(`/api/agent/tasks/user/count?planId=${planId}`).catch(() => null),
        fetchWithAuth("/api/connectors").catch(() => null),
        fetchWithAuth(`/api/design-authority/${planId}`).catch(() => null),
      ]);

      if (jobsResponse?.ok) {
        const jobsData = await jobsResponse.json().catch(() => ({}));
        setJobsCount(Number(jobsData.count || 0));
      }
      if (connectorsResponse?.ok) {
        const connectorsData = await connectorsResponse.json().catch(() => ({}));
        setConnectedIntegrations(
          (connectorsData.connectors || []).filter(
            connector => connector.id !== "web_search" && connector.status === "connected"
          )
        );
      }
      if (designAuthorityResponse?.ok) {
        const authorityData = await designAuthorityResponse.json().catch(() => ({}));
        const projectedActivity = (authorityData.design_activity || []).map(activity => ({
          ...activity,
          projectedStatus: governedTaskStatus({
            ...activity,
            calibration: {
              id: activity.calibration_event_id,
              human_disposition: activity.human_disposition,
            },
          }),
        }));
        const activeJobs = projectedActivity.filter(activity =>
          !["completed", "cancelled", "archived", "failed"].includes(activity.projectedStatus)
        );
        const needsAttention = activeJobs.some(activity => {
          return [
            "waiting_for_user",
            "waiting_for_approval",
            "awaiting_approval",
            "waiting_for_evidence",
            "waiting_for_credits",
            "blocked",
          ].includes(activity.projectedStatus);
        });
        setDesignAuthorityState({
          enabled: Boolean(authorityData.enabled),
          activeJobCount: activeJobs.length,
          needsAttention,
        });
      }
    };

    refreshOverviewMetadata();
    const interval = window.setInterval(refreshOverviewMetadata, 30000);
    window.addEventListener("focus", refreshOverviewMetadata);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshOverviewMetadata);
    };
  }, [planId, overviewRefreshKey]);

  // Cycle loading messages while generating
  useEffect(() => {
    if (!generating) return;
    let i = 0;
    const interval = setInterval(() => {
      i = (i + 1) % LOADING_MESSAGES.length;
      setLoadingMsg(LOADING_MESSAGES[i]);
    }, 2200);
    return () => clearInterval(interval);
  }, [generating]);

  const fetchPulse = useCallback(async () => {
    if (!planId || fetchingPlans.has(planId)) return;
    fetchingPlans.add(planId);
    const activePlanId = planId;
    setLoading(true);
    setPulse(null);
    setError(null);
    try {
      const res = await fetchWithAuth(`/api/pulse/${activePlanId}`);
      const data = await res.json();
      if (activePlanId !== currentPlanIdRef.current) return;
      if (data.pending_reminder) setRemindedAt(new Date(data.pending_reminder));
      if (data.pending_reminder_task_id) setRemindedTaskId(data.pending_reminder_task_id);
      if (data.navigation_summary) setNavigationSummary(data.navigation_summary);
      // Schedule is authoritative on every GET regardless of whether the pulse content
      // itself is stale — set it unconditionally so a pending regeneration below never
      // leaves scheduleSet/next_pulse_at looking empty and re-showing the setup picker.
      if (data.schedule) setSchedule(data.schedule);
      if (data.pulse && !data.needs_refresh) {
        setPulse(data.pulse);
      } else {
        // No pulse yet, or stale/new-data refresh needed — generate fresh
        await generatePulse(activePlanId, data.needs_refresh ? "auto_refresh" : "initial_load");
      }
    } catch (err) {
      console.error("Failed to fetch pulse:", err);
      if (activePlanId === currentPlanIdRef.current) setError("Couldn't load the pulse. Try again in a moment.");
    } finally {
      if (activePlanId === currentPlanIdRef.current) setLoading(false);
      fetchingPlans.delete(activePlanId);
    }
  }, [planId]);

  const generatePulse = async (activePlanId = planId, source = "manual_refresh") => {
    if (activePlanId !== currentPlanIdRef.current) return;
    setGenerating(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`/api/pulse/${activePlanId}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source }),
      });
      const data = await res.json();
      if (activePlanId !== currentPlanIdRef.current) return;

      if (data.pulse) {
        setPulse(data.pulse);
        if (data.navigation_summary) setNavigationSummary(data.navigation_summary);
        setOverviewRefreshKey(key => key + 1);
        return;
      }

      if (data.skipped && data.suppressedByLifecycle) {
        setError("Pulse is paused for this strategy status.");
        return;
      }

      // 409 means server-side generation is already in progress (setImmediate after
      // strategy save). Poll GET until that pulse lands instead of showing an error.
      if (res.status === 409 && data.generating) {
        for (let attempt = 0; attempt < 20; attempt++) {
          await new Promise(r => setTimeout(r, 3000));
          if (activePlanId !== currentPlanIdRef.current) return;
          const pollRes = await fetchWithAuth(`/api/pulse/${activePlanId}`);
          if (!pollRes) return;
          const pollData = await pollRes.json();
          if (activePlanId !== currentPlanIdRef.current) return;
          if (pollData.pulse) {
            setPulse(pollData.pulse);
            if (pollData.schedule) setSchedule(pollData.schedule);
            if (pollData.navigation_summary) setNavigationSummary(pollData.navigation_summary);
            setOverviewRefreshKey(key => key + 1);
            return;
          }
        }
        setError("Pulse is taking longer than expected. Try refreshing in a moment.");
        return;
      }

      setError(data.error || "Couldn't generate pulse. Try again shortly.");
    } catch (err) {
      console.error("Failed to generate pulse:", err);
      if (activePlanId === currentPlanIdRef.current) setError("Couldn't generate pulse. Try again in a moment.");
    } finally {
      if (activePlanId === currentPlanIdRef.current) setGenerating(false);
    }
  };

  useEffect(() => {
    fetchPulse();
  }, [fetchPulse, planId]);

  useEffect(() => {
    const handler = (e) => {
      if (!e.detail?.planId || String(e.detail.planId) === String(planId)) {
        fetchPulse();
      }
    };
    window.addEventListener('pabs:pulseScheduleUpdated', handler);
    window.addEventListener('pabs:githubBindingsUpdated', handler);
    return () => {
      window.removeEventListener('pabs:pulseScheduleUpdated', handler);
      window.removeEventListener('pabs:githubBindingsUpdated', handler);
    };
  }, [fetchPulse, planId]);


  const DESTINATION_ALIASES = {
    action_plan: "stepByStep",
    competitor_analysis: "competitor",
    brief: "strategy",
    product_strategy: "productStrategy",
    documents: "readme",
    overview: "slick",
  };
  const VALID_DESTINATIONS = new Set([
    "strategy",
    "productStrategy",
    "competitor",
    "decisions",
    "stepByStep",
    "calendar",
    "slick",
    "readme",
    "learnings",
    "pulse",
    "mcp",
    "share",
    "repository",
    "connectors",
    "designAuthority",
  ]);

  // Sections a Decisions CTA can scroll to, matching DecisionsView's section keys.
  const DECISIONS_SECTIONS = new Set(["strategicPosition", "blockers", "strategicRecommendations"]);

  const recordCtaClick = async (cta) => {
    if (!pulse?.id) return;
    const response = await fetchWithAuth(`/api/pulse/${planId}/${pulse.id}/cta-click`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ label: cta.label, action: cta.action, destination: cta.destination || cta.task_id || null }),
    });
    if (!response?.ok) throw new Error('Pulse action unavailable');
    return response.json();
  };

  const handleCTA = async (cta) => {
    if (cta.action === 'open_job') {
      try {
        const result = await recordCtaClick(cta);
        if (!result?.resolved_action) throw new Error('Missing current destination');
        cta = result.resolved_action;
      } catch {
        toast({title: 'This job is no longer available', description: 'Open Decisions to review the current work.', status: 'info', isClosable: true});
        onNavigateToView?.('decisions', {section:'blockers'});
        return;
      }
    } else {
      recordCtaClick(cta).catch(() => {});
    }
    const destination = DESTINATION_ALIASES[cta.destination] || cta.destination;
    if (cta.action === "open_job" && cta.task_id) {
      window.dispatchEvent(new CustomEvent("pabs:openJobs", {
        detail: { taskId: cta.task_id },
      }));
    } else if (cta.action === "navigate" && destination === "repository") {
      window.dispatchEvent(new CustomEvent("pabs:openGitHubRepositories", { detail: { planId } }));
    } else if (cta.action === "navigate" && VALID_DESTINATIONS.has(destination)) {
      const section = destination === "decisions" && DECISIONS_SECTIONS.has(cta.section) ? cta.section : null;
      onNavigateToView?.(destination, section ? { section } : null);
    } else if (cta.action === "open_chat" && onSendChatPrompt) {
      onSendChatPrompt(cta.context || "");
    } else if (cta.action === "set_strategy_status" && STRATEGY_STATUS_LABELS[cta.status]) {
      if (cta.status === strategyLifecycleStatus) return;
      if (onStrategyLifecycleStatusChange) {
        const result = await onStrategyLifecycleStatusChange(cta.status, "pulse");
        if (result?.ok !== false && onSendChatPrompt) {
          onSendChatPrompt(buildStrategyStatusChatPrompt(cta.status));
        }
        return;
      }
      setStatusUpdating(true);
      try {
        const res = await fetchWithAuth(`/api/plans/${planId}/lifecycle-status`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: cta.status, source: "pulse" }),
        });
        if (!res?.ok) throw new Error("Status update failed");
        toast({
          title: `Marked as ${STRATEGY_STATUS_LABELS[cta.status]}`,
          status: "success",
          duration: 2200,
          isClosable: true,
        });
        if (onSendChatPrompt) {
          onSendChatPrompt(buildStrategyStatusChatPrompt(cta.status));
        }
      } catch (err) {
        console.error("Failed to update strategy status:", err);
        toast({
          title: "Couldn't update status",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setStatusUpdating(false);
      }
    } else if (cta.action === "schedule_reminder" || cta.action === "schedule_email_reminder") {
      const days = cta.days || 3;
      try {
        const res = await fetchWithAuth(`/api/pulse/${planId}/remind`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ days, context: cta.context || "" }),
        });
        if (!res?.ok) throw new Error("Failed");
        const responseData = await res.json().catch(() => ({}));
        const remindDate = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
        const label = remindDate.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
        setRemindedAt(remindDate);
        setRemindedTaskId(responseData.task_id || null);
        toast({
          duration: 5000,
          isClosable: true,
          render: ({ onClose }) => (
            <Box bg="gray.800" color="white" px={4} py={3} borderRadius="md" boxShadow="lg">
              <HStack justify="space-between" align="start" spacing={3}>
                <Box>
                  <Text fontWeight="600" fontSize="sm">Reminder set for {label}</Text>
                  <Text fontSize="xs" opacity={0.75} mt={0.5}>PABS will follow up by email. Your check-in schedule is unchanged.</Text>
                </Box>
                <Text fontSize="sm" cursor="pointer" opacity={0.8} onClick={onClose} flexShrink={0}>✕</Text>
              </HStack>
            </Box>
          ),
        });
      } catch {
        toast({ title: "Couldn't schedule reminder", status: "error", duration: 3000, isClosable: true });
      }
    } else if (cta.action === "navigate" && cta.destination && onSendChatPrompt) {
      // Model hallucinated a destination — fall back to chat with the label as context
      onSendChatPrompt(cta.context || cta.label || "");
    }
  };

  const SOURCE_LABELS = {
    brief:               "Brief",
    action_plan:         "Action Plan",
    product_strategy:    "Product Strategy",
    competitor_analysis: "Competitor Analysis",
    mcp_connection:      "Pabs MCP connection",
    mcp_activity:        "Pabs MCP activity",
    learnings:           "Learnings",
    exports:             "Documents",
    previous_pulses:     "Previous pulses",
    connector_notion_activity:  "Notion activity",
    connector_granola_activity: "Granola activity",
    connector_github_activity:  "GitHub activity",
    connector_github_check:     "GitHub checked",
    connector_github_signals:   "GitHub delivery evidence",
    github_repository_binding:  "GitHub repository",
    github_execution_evidence:  "GitHub delivery evidence",
  };

  // ---------------------------------------------------------------------------
  // Loading / generating skeleton
  // ---------------------------------------------------------------------------
  if (loading || generating) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center"
           minH="80vh" px={isNarrow ? 4 : 8} pb={20}
           sx={{ "@keyframes shimmer": { "0%,100%": { opacity: 0.35 }, "50%": { opacity: 0.65 } } }}>
        <Box w="full" maxW={COMPACT_CONTENT_MAX_WIDTH}>
          {/* Status above card */}
          <HStack spacing={2} mb={4} justify="center">
            <Spinner size="xs" color="gray.400" />
            <Text fontSize="xs" color="gray.400">{loadingMsg}</Text>
          </HStack>
          <Box
            bg="gray.100"
            borderRadius="xl"
            {...ACCENT_CARD_CONTENT_PROPS}
            boxShadow="0 2px 12px rgba(0,0,0,0.06)"
          >
            {/* Header row — label left, two icon placeholders right */}
            <HStack justify="space-between" mb={5}>
              <Box h="8px" w="44%" bg="gray.200" borderRadius="full"
                   sx={{ animation: "shimmer 1.8s ease-in-out infinite" }} />
              <HStack spacing={2}>
                <Box h="16px" w="16px" bg="gray.200" borderRadius="sm"
                     sx={{ animation: "shimmer 1.8s ease-in-out infinite 0.1s" }} />
                <Box h="16px" w="16px" bg="gray.200" borderRadius="sm"
                     sx={{ animation: "shimmer 1.8s ease-in-out infinite 0.15s" }} />
              </HStack>
            </HStack>

            {/* Paragraph 1 — 2 lines */}
            <Box h="9px" w="100%" bg="gray.200" borderRadius="full" mb={2}
                 sx={{ animation: "shimmer 1.8s ease-in-out infinite 0.1s" }} />
            <Box h="9px" w="72%" bg="gray.200" borderRadius="full" mb={5}
                 sx={{ animation: "shimmer 1.8s ease-in-out infinite 0.15s" }} />

            {/* Paragraph 2 — 2 lines */}
            <Box h="9px" w="96%" bg="gray.300" borderRadius="full" mb={2}
                 sx={{ animation: "shimmer 1.8s ease-in-out infinite 0.2s" }} />
            <Box h="9px" w="84%" bg="gray.300" borderRadius="full" mb={5}
                 sx={{ animation: "shimmer 1.8s ease-in-out infinite 0.25s" }} />

            {/* Paragraph 3 — 1 line */}
            <Box h="9px" w="88%" bg="gray.200" borderRadius="full" mb={6}
                 sx={{ animation: "shimmer 1.8s ease-in-out infinite 0.3s" }} />

            {/* CTA — one primary button shape */}
            <Box h="44px" w="42%" bg="gray.200" borderRadius="full"
                 sx={{ animation: "shimmer 1.8s ease-in-out infinite 0.35s" }} />
          </Box>
        </Box>
      </Box>
    );
  }

  if (error || !pulse) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="60vh" px={4}>
        <VStack spacing={3}>
          <Text fontSize="sm" color="gray.400">{error || "Couldn't load the pulse. Try again in a moment."}</Text>
          {planId && (
            <Button size="sm" variant="outline" colorScheme="gray" onClick={fetchPulse}>
              Try again
            </Button>
          )}
        </VStack>
      </Box>
    );
  }

  const rawCtaActions = pulse.cta_actions || [];
  const hasNonStatusCta = rawCtaActions.some(cta => cta.action !== "set_strategy_status");
  // Lifecycle choices are a complete Pulse mode, never a secondary action.
  // This also cleans up older stored pulses created before the backend guard.
  const ctaActions = hasNonStatusCta
    ? rawCtaActions.filter(cta => cta.action !== "set_strategy_status")
    : rawCtaActions;
  const allStatusCtas = ctaActions.length > 0 && ctaActions.every(cta => cta.action === "set_strategy_status");
  const sources    = pulse.sources_used || [];
  const sourceLabels = sources.map(source => SOURCE_LABELS[source] || source);
  const visibleSources = sourceLabels.slice(0, 4);
  const additionalSources = sourceLabels.slice(4);
  const isPulseScheduled = Boolean(schedule?.next_pulse_at);

  const createdDate = new Date(pulse.created_at).toLocaleDateString("en-NZ", {
    day: "numeric", month: "long", year: "numeric",
  }) + " " + new Date(pulse.created_at).toLocaleTimeString("en-NZ", {
    hour: "numeric", minute: "2-digit", hour12: true,
  });

  // ---------------------------------------------------------------------------
  // Render — centered card
  // ---------------------------------------------------------------------------
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="flex-start"
      minH="80vh"
      pt={isNarrow ? 10 : "12vh"}
      px={isNarrow ? 4 : 8}
      pb={20}
      sx={{
        "@keyframes fadeUp": {
          from: { opacity: 0, transform: "translateY(8px)" },
          to:   { opacity: 1, transform: "translateY(0)" },
        },
        animation: "fadeUp 0.35s ease-out",
      }}
    >
      <Box w="full" maxW={COMPACT_CONTENT_MAX_WIDTH}>

        <Box
          role="group"
          position="relative"
          data-actions-dismissed={floatingActionsDismissed}
          onMouseLeave={() => setFloatingActionsDismissed(false)}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              setFloatingActionsDismissed(false);
            }
          }}
          sx={{
            // Keep hover active while the pointer crosses from the card to the
            // floating action rail. Without this bridge, the small visual gap
            // becomes a dead zone and dismisses the controls before a user can
            // reach them.
            "&::after": {
              content: '""',
              position: "absolute",
              zIndex: 1,
              top: 0,
              right: "-48px",
              width: "48px",
              height: "104px",
              display: { base: "none", md: "block" },
            },
            '&:hover:not([data-actions-dismissed="true"]) [data-floating-card-actions], &:focus-within:not([data-actions-dismissed="true"]) [data-floating-card-actions]': {
              opacity: 1,
              transform: "translate(0, 0)",
              pointerEvents: "auto",
            },
            "&:hover [data-pulse-time], &:focus-within [data-pulse-time]": {
              opacity: 1,
              transform: "translateY(0)",
            },
            "&:hover [data-pulse-sources], &:focus-within [data-pulse-sources]": {
              opacity: 1,
              transform: "translateY(0)",
              pointerEvents: "auto",
            },
            "@media (hover: none)": {
              "& [data-floating-card-actions]": {
                opacity: 1,
                transform: "translate(0, 0)",
                pointerEvents: "auto",
              },
              "& [data-pulse-time]": {
                opacity: 1,
                transform: "translateY(0)",
              },
              "& [data-pulse-sources]": {
                opacity: 1,
                transform: "translateY(0)",
                pointerEvents: "auto",
              },
            },
            "@media (prefers-reduced-motion: reduce)": {
              "& [data-pulse-time], & [data-pulse-sources]": {
                transition: "none",
              },
            },
          }}
        >
          <FloatingCardActions
            actions={[
              {
                label: "Refresh pulse",
                icon: "refresh",
                onClick: () => {
                  setFloatingActionsDismissed(true);
                  generatePulse(planId, "manual_refresh");
                },
              },
              {
                label: isPulseScheduled ? "Pulse settings" : "Schedule recurring Pulse",
                icon: isPulseScheduled ? "settings" : "calendar_month",
                onClick: () => {
                  setFloatingActionsDismissed(true);
                  window.dispatchEvent(new CustomEvent("pabs:openJobs", {
                    detail: { tab: "Routines", taskId: `pulse-routine-${planId}` },
                  }));
                },
              },
            ]}
          />

          {/* Card — dark */}
          <Box
            bg={ACCENT_CARD_BACKGROUND}
            borderTopLeftRadius="48px"
            borderTopRightRadius="48px"
            borderBottomLeftRadius="48px"
            borderBottomRightRadius="0px"
            {...ACCENT_CARD_CONTENT_PROPS}
            boxShadow="0 4px 24px rgba(0,0,0,0.22)"
          >
            {/* Header */}
            <HStack justify="space-between" align="flex-start" mb={5}>
              <VStack align="flex-start" spacing={0}>
                <AccentCardLabel>Strategy</AccentCardLabel>
                <AccentCardLabel>Pulse</AccentCardLabel>
              </VStack>
              <Text
                data-pulse-time
                fontSize="xs"
                color="whiteAlpha.700"
                opacity={0}
                transform="translateY(-4px)"
                transition="opacity 180ms ease, transform 180ms ease"
              >
                {createdDate}
              </Text>
            </HStack>

            {/* Pulse content */}
            <Box>
              <PabsMessageText communication={pulse.communication} fallback={pulse.content} renderMessage={message => <PulseMarkdown content={message} dark/>}/>
            </Box>

            {/* CTA */}
            {ctaActions.length > 0 && (
              <Box mt={6} display="flex" flexWrap="wrap" gap={2}>
                {ctaActions.map((cta, i) => {
                const isStatusCta = cta.action === "set_strategy_status" && STRATEGY_STATUS_LABELS[cta.status];
                const isSelectedStatus = isStatusCta && cta.status === strategyLifecycleStatus;
                const isPrimaryCta = !allStatusCtas && i === 0;
                const isFilled = isPrimaryCta || isSelectedStatus;
                const isReminderCta = cta.action === "schedule_reminder" || cta.action === "schedule_email_reminder";
                const isReminded = isReminderCta && !!remindedAt;
                // Already scheduled — this is a status readout, not a call to
                // action, so it must never carry the same visual weight as the
                // primary CTA. Style it like any other secondary/outline
                // action, and — when a job actually exists for it — make it a
                // real link to that job instead of a dead, disabled pill.
                const reminderLabel = isReminded
                  ? `Scheduled for ${remindedAt.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
                  : cta.label;
                const isLinkedReminder = isReminded && !!remindedTaskId;
                return (
                  <Button
                    key={i}
                    size="md"
                    minH="44px"
                    px={isPrimaryCta ? 5 : 4}
                    variant={isFilled && !isReminded ? "solid" : "outline"}
                    bg={isFilled && !isReminded ? "whiteAlpha.900" : "transparent"}
                    color={isReminded ? "whiteAlpha.600" : isFilled ? "gray.900" : "whiteAlpha.900"}
                    borderColor={isReminded ? "whiteAlpha.300" : isFilled ? "transparent" : "whiteAlpha.400"}
                    borderRadius="full"
                    fontSize="sm"
                    fontWeight={isFilled && !isReminded ? "semibold" : "medium"}
                    leftIcon={isReminded ? <span className="material-icons-outlined" style={{ fontSize: "14px" }}>schedule</span> : undefined}
                    onClick={() => {
                      if (isReminded) {
                        if (isLinkedReminder) {
                          window.dispatchEvent(new CustomEvent("pabs:openJobs", { detail: { taskId: remindedTaskId } }));
                        }
                        return;
                      }
                      handleCTA(cta);
                    }}
                    isLoading={isStatusCta && statusUpdating}
                    isDisabled={isReminded && !isLinkedReminder}
                    cursor={isReminded ? (isLinkedReminder ? "pointer" : "default") : "pointer"}
                    _hover={isReminded
                      ? (isLinkedReminder ? { bg: "whiteAlpha.100", color: "whiteAlpha.900", borderColor: "whiteAlpha.500" } : {})
                      : isFilled ? { bg: "white" } : { bg: "whiteAlpha.100", color: "whiteAlpha.900" }}
                  >
                    {isStatusCta ? STRATEGY_STATUS_LABELS[cta.status] : reminderLabel}
                  </Button>
                );
                })}
              </Box>
            )}
          </Box>

          {/* Sources sit outside the card surface. */}
          {sources.length > 0 && (
            <Box
              data-pulse-sources
              mt={3}
              px={{ base: 2, md: 4 }}
              opacity={0}
              transform="translateY(-5px)"
              pointerEvents="none"
              transition="opacity 180ms ease, transform 180ms ease"
            >
              <Flex gap={2} align="center" flexWrap="wrap">
                <Text fontSize="xs" color="gray.400">Sources:</Text>
                {visibleSources.map((source, index) => (
                  <React.Fragment key={`${source}-${index}`}>
                    {index > 0 && <Text fontSize="xs" color="gray.300">·</Text>}
                    <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">{source}</Text>
                  </React.Fragment>
                ))}
                {additionalSources.length > 0 && (
                  <HStack
                    as="button"
                    type="button"
                    spacing={0.5}
                    color="gray.400"
                    cursor="pointer"
                    aria-label={sourcesExpanded ? "Hide additional sources" : "Show all sources"}
                    aria-expanded={sourcesExpanded}
                    onClick={() => setSourcesExpanded(expanded => !expanded)}
                    _hover={{ color: "gray.700" }}
                  >
                    <Text fontSize="xs">...</Text>
                    <Box
                      as="span"
                      className="material-icons-outlined"
                      fontSize="14px"
                      transform={sourcesExpanded ? "rotate(180deg)" : "rotate(0deg)"}
                      transition="transform 0.15s ease"
                    >
                      expand_more
                    </Box>
                  </HStack>
                )}
              </Flex>
              <Collapse in={sourcesExpanded} animateOpacity>
                <Flex mt={1.5} gap={1.5} flexWrap="wrap" align="center">
                  {additionalSources.map((source, index) => (
                    <React.Fragment key={`${source}-${index}`}>
                      {index > 0 && <Text fontSize="xs" color="gray.300">·</Text>}
                      <Text fontSize="xs" color="gray.500">{source}</Text>
                    </React.Fragment>
                  ))}
                </Flex>
              </Collapse>
            </Box>
          )}
        </Box>

        <Flex
          mt={{ base: 12, md: 16 }}
          px={{ base: 0, md: 1 }}
          w="max-content"
          maxW="none"
          position="relative"
          left="50%"
          transform="translateX(-50%)"
          align="center"
          justify="center"
          gap={1.5}
          flexWrap="nowrap"
          whiteSpace="nowrap"
          overflow="visible"
          color="gray.500"
          fontSize="xs"
          css={{
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {[
            {
              label: designAuthorityState.enabled
                ? (designAuthorityState.activeJobCount ? `${designAuthorityState.activeJobCount} active design reviews` : "Design guidance on")
                : "Design Authority OFF",
              icon: "verified_user",
              isWarning: !designAuthorityState.enabled,
              statusDot: designAuthorityState.enabled
                ? (designAuthorityState.needsAttention ? "orange.400" : "green.400")
                : "orange.500",
              onClick: () => onNavigateToView?.("designAuthority"),
            },
            {
              label: `${navigationSummary.blockers} Blockers`,
              icon: "report_problem",
              onClick: () => onNavigateToView?.("decisions", { section: "blockers" }),
            },
            {
              label: `${navigationSummary.next_steps} Next steps`,
              icon: "route",
              onClick: () => onNavigateToView?.("decisions", { section: "strategicRecommendations" }),
            },
            {
              label: `${navigationSummary.learnings} Learnings`,
              icon: "lightbulb",
              onClick: () => onNavigateToView?.("learnings"),
            },
            {
              label: `${jobsCount} Jobs`,
              icon: "work_outline",
              onClick: () => window.dispatchEvent(new CustomEvent("pabs:openJobs", { detail: { tab: "Tasks" } })),
            },
            {
              label: connectedIntegrations.length > 0
                ? `${connectedIntegrations.length} Integrations`
                : "Integrations",
              icon: "hub",
              integrations: connectedIntegrations,
              onClick: () => onNavigateToView?.("connectors"),
            },
          ].map((item, index) => (
            <HStack key={item.label} spacing={1.5} flexShrink={0}>
              {index > 0 && <Text as="span" flexShrink={0} aria-hidden="true" color="gray.400">·</Text>}
              <Button
                variant="unstyled"
                minW="auto"
                h="auto"
                flexShrink={0}
                display="inline-flex"
                alignItems="center"
                gap={1.5}
                color={item.isWarning ? "orange.800" : "inherit"}
                bg={item.isWarning ? "orange.100" : "transparent"}
                px={item.isWarning ? 2.5 : 0}
                py={item.isWarning ? 1 : 0}
                borderRadius={item.isWarning ? "full" : 0}
                fontSize="inherit"
                fontWeight={item.isWarning ? "600" : "normal"}
                lineHeight="1.5"
                onClick={item.onClick}
                _hover={item.isWarning
                  ? { color: "orange.900", bg: "orange.200" }
                  : { color: "gray.700" }}
                _focusVisible={{ boxShadow: "outline", borderRadius: "sm" }}
              >
                <Box
                  as="span"
                  position="relative"
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  aria-hidden="true"
                >
                  <Box
                    as="span"
                    className="material-icons-outlined"
                    fontSize="17px"
                    color="currentColor"
                  >
                    {item.icon}
                  </Box>
                  {item.statusDot && (
                    <Box
                      as="span"
                      position="absolute"
                      right="-2px"
                      top="-2px"
                      w="7px"
                      h="7px"
                      borderRadius="full"
                      bg={item.statusDot}
                      border="1.5px solid"
                      borderColor={item.isWarning ? "orange.100" : "gray.50"}
                    />
                  )}
                </Box>
                {item.integrations?.length > 0 && (
                  <ConnectedIntegrationIcons integrations={item.integrations} />
                )}
                <Text as="span">{item.label}</Text>
              </Button>
            </HStack>
          ))}
        </Flex>

        {/* Previous pulses */}
        <PulseArchive planId={planId} />
      </Box>
    </Box>
  );
};

export default PulseView;
export { PulseSettingsForm };
